import apiClient from "./api";
import eventi from "./eventi";

const BASE = "/pms";

/**
 * Il nome del file dall'intestazione Content-Disposition.
 *
 * Si guarda prima filename*, che e' quello codificato e regge gli accenti; filename semplice
 * e' la riserva. Senza nessuno dei due si lascia decidere al browser.
 */
function nomeDaIntestazione(intestazioni) {
  const riga = (intestazioni || {})["content-disposition"] || "";
  const codificato = /filename\*=UTF-8''([^;]+)/i.exec(riga);
  if (codificato) {
    try {
      return decodeURIComponent(codificato[1]);
    } catch (e) {
      // Intestazione malformata: si continua con la riserva.
    }
  }
  const semplice = /filename="?([^";]+)"?/i.exec(riga);
  return semplice ? semplice[1] : "allegato";
}

/** La lettura dello stato WhatsApp in corso, condivisa da chi la chiede nello stesso momento. */
let statoWhatsappInVolo = null;

/**
 * L'ultimo stato WhatsApp letto, e quando. Lo chiedono il banner e la casella della pagina Messaggi, e la
 * scheda di ogni prenotazione che si apre (anche dalla finestra del calendario, a ogni clic): senza un
 * ricordo breve erano due o tre richieste a ogni apertura, anche negli spazi senza WhatsApp.
 */
let statoWhatsappLetto = null;
const STATO_WHATSAPP_VALE_MS = 30000;

/**
 * Lo stato cambia sul server e lo annuncia ("whatsapp"); dopo una caduta del canale gli annunci persi non
 * tornano ("riconnesso"). In tutti e due i casi il ricordo si butta, e anche la lettura in volo, che e'
 * partita prima del cambio: iscritto qui, al caricamento del modulo, cioe' prima delle schermate, e
 * quindi prima che le loro riletture lo chiedano di nuovo.
 */
function dimenticaStatoWhatsapp() {
  statoWhatsappLetto = null;
  statoWhatsappInVolo = null;
}
eventi.su("whatsapp", dimenticaStatoWhatsapp);
eventi.su("riconnesso", dimenticaStatoWhatsapp);

/** Le lingue che si possono scegliere per l'ospite, lette una volta sola: vedi lingueOspite. */
let lingueOspiteInVolo = null;

/**
 * La struttura da mettere nel percorso di una chiamata dei messaggi, controllata prima di partire.
 *
 * <p>Il caso vero (v1.1.0, 26/09/2026): toccando la push di un WhatsApp nell'app la pagina Messaggi
 * apriva una conversazione senza struttura e senza filtro, e nel percorso finiva la parola "undefined".
 * Il server rispondeva 500 "Unexpected error", e l'app mostrava "internal error". Le schermate ora la
 * struttura la cercano prima di chiamare; questa e' la rete sotto: una chiamata cosi' non parte, e chi
 * chiama riceve un errore suo (code STRUTTURA_MANCANTE), senza risposta del server, invece di un 400.</p>
 */
function struttura(propertyId) {
  const s = propertyId == null ? "" : String(propertyId).trim();
  if (!s || s === "undefined" || s === "null") {
    const e = new Error("Struttura della conversazione non nota");
    e.code = "STRUTTURA_MANCANTE";
    throw e;
  }
  return encodeURIComponent(s);
}

export default {
  // Lifecycle
  async enable(propertyId, settings = null) {
    const { data } = await apiClient.post(`${BASE}/properties/${propertyId}/enable`, settings);
    return data?.data;
  },
  async disable(propertyId) {
    const { data } = await apiClient.post(`${BASE}/properties/${propertyId}/disable`);
    return data?.data;
  },
  async fullSync(propertyId) {
    const { data } = await apiClient.post(`${BASE}/properties/${propertyId}/full-sync`);
    return data?.data;
  },
  async syncBookings(propertyId) {
    const { data } = await apiClient.post(`${BASE}/properties/${propertyId}/bookings/sync`);
    return data?.data;
  },
  async resyncStructure(propertyId) {
    const { data } = await apiClient.post(`${BASE}/properties/${propertyId}/resync-structure`);
    return data?.data;
  },
  async updateChannexSettings(propertyId, settings) {
    const { data } = await apiClient.put(`${BASE}/properties/${propertyId}/channex-settings`, settings);
    return data?.data;
  },

  // Units & rate plans
  async createRoomType(propertyId, body) {
    const { data } = await apiClient.post(`${BASE}/properties/${propertyId}/room-types`, body);
    return data?.data;
  },
  async updateRoomType(roomTypeId, body) {
    const { data } = await apiClient.put(`${BASE}/room-types/${roomTypeId}`, body);
    return data?.data;
  },
  async deleteRoomType(roomTypeId) {
    const { data } = await apiClient.delete(`${BASE}/room-types/${roomTypeId}`);
    return data?.data;
  },
  async createRatePlan(roomTypeId, body) {
    const { data } = await apiClient.post(`${BASE}/room-types/${roomTypeId}/rate-plans`, body);
    return data?.data;
  },
  async updateRatePlan(ratePlanId, body) {
    const { data } = await apiClient.put(`${BASE}/rate-plans/${ratePlanId}`, body);
    return data?.data;
  },
  async deleteRatePlan(ratePlanId) {
    const { data } = await apiClient.delete(`${BASE}/rate-plans/${ratePlanId}`);
    return data?.data;
  },

  // Calendar
  async getCalendar(propertyId, from, to) {
    const { data } = await apiClient.get(`${BASE}/properties/${propertyId}/calendar`, {
      params: { from, to, _t: Date.now() }, // cache-buster: always fetch fresh after edits
    });
    return data?.data;
  },
  /**
   * L'ordine con cui le strutture si presentano nel calendario.
   *
   * Si manda l'elenco completo degli identificativi nella sequenza voluta: il server numera da
   * zero. Mandare l'intera sequenza invece di "questa e' salita di due" evita che due schede
   * aperte insieme si sovrascrivano a meta'.
   */
  async salvaOrdineStrutture(ordine) {
    await apiClient.put(`${BASE}/ordine`, { ordine });
  },
  async getCalendarAll(from, to) {
    const { data } = await apiClient.get(`${BASE}/calendar`, {
      params: { from, to, _t: Date.now() },
    });
    return data?.data;
  },
  async applyRates(ratePlanId, body) {
    const { data } = await apiClient.post(`${BASE}/rate-plans/${ratePlanId}/rates`, body);
    return data?.data;
  },
  // Piu' modifiche in una sola richiesta: il backend le applica in un'unica transazione
  // e verso il channel manager parte un solo aggiornamento.
  async applyRatesBulk(items) {
    const { data } = await apiClient.post(`${BASE}/rates/bulk`, { items });
    return data?.data;
  },
  // Aggiornamenti che non sono mai arrivati ai canali: in condizioni normali elenco vuoto.
  async syncErrors() {
    const { data } = await apiClient.get(`${BASE}/sync-errors`);
    return data?.data;
  },
  async syncLog(propertyId, limit = 50) {
    const params = { limit };
    if (propertyId) params.propertyId = propertyId;
    const { data } = await apiClient.get(`${BASE}/sync-log`, { params });
    return data?.data;
  },
  // units a null toglie l'impostazione manuale: torna a valere il totale camere.
  async setInventory(roomTypeId, body) {
    const { data } = await apiClient.post(`${BASE}/room-types/${roomTypeId}/availability`, body);
    return data?.data;
  },
  async block(roomTypeId, body) {
    const { data } = await apiClient.post(`${BASE}/room-types/${roomTypeId}/blocks`, body);
    return data?.data;
  },
  async removeBlock(blockId) {
    const { data } = await apiClient.delete(`${BASE}/blocks/${blockId}`);
    return data?.data;
  },
  // Chiude la struttura. Senza body.camere tutte le camere attive per intero, come sempre; con
  // body.camere = [{ roomTypeId, unita }] solo quelle, e unita nulla vuol dire tutte le unita'.
  async blockProperty(propertyId, body) {
    const { data } = await apiClient.post(`${BASE}/properties/${propertyId}/blocks`, body);
    return data?.data;
  },
  // L'aggiornamento in blocco: camere, tariffe, periodi, giorni della settimana e azioni in un
  // salvataggio solo. Torna quante ne ha applicate e quali ha saltato, con il motivo.
  async aggiornaInventario(body) {
    const { data } = await apiClient.post(`${BASE}/inventory/bulk`, body);
    return data?.data;
  },

  // Channels
  // Lo stato reale della distribuzione: la modalita' che teniamo noi dice solo cosa
  // abbiamo deciso, non se la struttura esiste ancora su Channex.
  async getDistributionStatus(propertyId) {
    const { data } = await apiClient.get(`${BASE}/properties/${propertyId}/distribution-status`);
    return data?.data || null;
  },
  async getChannels(propertyId) {
    const { data } = await apiClient.get(`${BASE}/properties/${propertyId}/channels`);
    return data?.data || [];
  },
  async removeChannel(propertyId, channelId) {
    const { data } = await apiClient.delete(`${BASE}/properties/${propertyId}/channels/${channelId}`);
    return data?.data;
  },
  // I canali che si mappano per camera, tariffa e occupazione: Booking.com ed Expedia.
  // Il canale e' un parametro perche' la procedura e' identica: cambia solo a chi si parla.
  async previewBooking(propertyId, canale, hotelId) {
    const { data } = await apiClient.post(
      `${BASE}/properties/${propertyId}/channels/${canale}/preview`, { hotelId });
    return data?.data;
  },
  // La mappatura in vigore di un canale gia' collegato, per poterla rivedere.
  async getChannelMapping(propertyId, channelId) {
    const { data } = await apiClient.get(`${BASE}/properties/${propertyId}/channels/${channelId}/mapping`);
    return data?.data;
  },
  async updateChannelMapping(propertyId, channelId, ratePlans) {
    const { data } = await apiClient.put(
      `${BASE}/properties/${propertyId}/channels/${channelId}/mapping`, { ratePlans });
    return data?.data;
  },
  async connectBooking(propertyId, canale, payload) {
    const { data } = await apiClient.post(`${BASE}/properties/${propertyId}/channels/${canale}`, payload);
    return data?.data;
  },
  // Vrbo: accesso con doppio fattore, in tre passi.
  // Il gettone torna qui e si rimanda al passo successivo: non viene mai salvato dal server,
  // e quando la finestra si chiude sparisce. Vale anche per utente e password.
  async vrboAccesso(propertyId, username, password) {
    const { data } = await apiClient.post(
      `${BASE}/properties/${propertyId}/channels/vrbo/accesso`, { username, password });
    return data?.data;
  },
  async mappaturaVrbo(propertyId, channelId) {
    const { data } = await apiClient.get(
      `${BASE}/properties/${propertyId}/channels/${channelId}/vrbo/mappatura`);
    return data?.data || [];
  },
  async salvaMappaturaVrbo(propertyId, channelId, annunci) {
    await apiClient.put(
      `${BASE}/properties/${propertyId}/channels/${channelId}/vrbo/mappatura`, { annunci });
  },
  async vrboChiediCodice(propertyId, token, indice, etichetta) {
    const { data } = await apiClient.post(
      `${BASE}/properties/${propertyId}/channels/vrbo/codice`, { token, indice, etichetta });
    return data?.data;
  },
  async vrboVerificaCodice(propertyId, token, codice) {
    const { data } = await apiClient.post(
      `${BASE}/properties/${propertyId}/channels/vrbo/verifica`, { token, codice });
    return data?.data;
  },
  async vrboAnnunci(propertyId, credenziali) {
    const { data } = await apiClient.post(
      `${BASE}/properties/${propertyId}/channels/vrbo/annunci`, credenziali);
    return data?.data;
  },
  async connectVrbo(propertyId, payload) {
    const { data } = await apiClient.post(`${BASE}/properties/${propertyId}/channels/vrbo`, payload);
    return data?.data;
  },
  // Airbnb native connection (OAuth)
  async getAirbnbOAuthUrl(propertyId, body) {
    const { data } = await apiClient.post(`${BASE}/properties/${propertyId}/channels/airbnb/init`, body);
    return data?.data?.authorizeUrl;
  },
  async getAirbnbListings(propertyId, channelId) {
    const { data } = await apiClient.get(`${BASE}/properties/${propertyId}/channels/${channelId}/airbnb/listings`);
    return data?.data || [];
  },
  async mapAirbnb(propertyId, channelId, body) {
    const { data } = await apiClient.post(`${BASE}/properties/${propertyId}/channels/${channelId}/airbnb/mappings`, body);
    return data?.data;
  },
  async unmapAirbnb(propertyId, channelId, mappingId) {
    const { data } = await apiClient.delete(`${BASE}/properties/${propertyId}/channels/${channelId}/airbnb/mappings/${mappingId}`);
    return data?.data;
  },
  // Channel lifecycle
  async activateChannel(propertyId, channelId) {
    const { data } = await apiClient.post(`${BASE}/properties/${propertyId}/channels/${channelId}/activate`);
    return data?.data;
  },
  async deactivateChannel(propertyId, channelId) {
    const { data } = await apiClient.post(`${BASE}/properties/${propertyId}/channels/${channelId}/deactivate`);
    return data?.data;
  },

  // Messaggi
  // Le conversazioni: oggi una per thread OTA, domani potranno raggruppare anche WhatsApp.
  // forza: vero quando e' l'utente ad aprire la schermata, cosi' si salta il freno sul
  // riallineamento con Channex; falso sugli aggiornamenti automatici.
  async getConversations(propertyId, forza = false) {
    const { data } = await apiClient.get(`${BASE}/properties/${propertyId}/conversations`,
      { params: { forza } });
    return data?.data || [];
  },
  /**
   * I messaggi di una conversazione. canale ("OTA" o "WHATSAPP"): solo le bolle di quel canale, come le
   * mostrano le schede Portali e WhatsApp della pagina Messaggi; null, tutte (la scheda Tutti, la scheda
   * della prenotazione).
   */
  async getConversation(propertyId, conversationId, { canale = null } = {}) {
    const { data } = await apiClient.get(
      `${BASE}/properties/${struttura(propertyId)}/conversations/${encodeURIComponent(conversationId)}`,
      { params: { canale: canale || undefined } });
    return data?.data || [];
  },
  /**
   * Un'immagine allegata a un messaggio dell'ospite.
   *
   * Torna un blob e non un indirizzo perche' l'endpoint vuole il token: un <img src="..."> che
   * punti dritto all'API non porterebbe l'intestazione di autorizzazione e riceverebbe un 401.
   * Con scarica=true il server manda anche il nome con cui salvarlo.
   */
  async allegatoMessaggio(messaggioId, indice, scarica = false) {
    const risposta = await apiClient.get(
      `${BASE}/messaggi/${messaggioId}/allegati/${indice}`,
      { params: { scarica }, responseType: "blob" }
    );
    return { blob: risposta.data, nome: nomeDaIntestazione(risposta.headers) };
  },
  async getMessages(propertyId) {
    const { data } = await apiClient.get(`${BASE}/properties/${propertyId}/messages`);
    return data?.data;
  },
  // proprieta: elenco di id; vuoto vuol dire tutte quelle dell'utenza.
  // skipToast: per chi le conversazioni le legge solo di contorno (la pagina Test funzionalita'
  // segna le prenotazioni che ne hanno una) e non vuole un errore a schermo se non rispondono.
  async getAllConversations(forza = false, proprieta = [], { skipToast = false } = {}) {
    const { data } = await apiClient.get(`${BASE}/conversations`, {
      params: { forza, proprieta: proprieta.length ? proprieta.join(",") : undefined },
      skipToast,
    });
    return data?.data || [];
  },
  /**
   * Una pagina dell'elenco: le ultime `limite` conversazioni, piu' quelle che non si possono
   * lasciare fuori (da associare, con messaggi da leggere). Torna { conversazioni, altre }.
   *
   * Con limite null chiede l'elenco intero, come getAllConversations: serve quando si cerca una
   * conversazione precisa che non e' nella pagina. Un backend che il limite non lo conosce ancora
   * risponde con l'elenco intero, e anche quello si legge qui: nessun ordine obbligato fra i due
   * rilasci.
   */
  //
  // allinea=false: solo dal database, senza riallineare con Channex. E' come si apre la casella:
  // l'elenco subito, e il riallineamento dopo, in sottofondo.
  //
  // canale: "WHATSAPP" o "OTA" per le sole conversazioni vive su quel canale (le schede della pagina
  // Messaggi); null, tutte. Il filtro lo fa il server perche' l'elenco e' a pagine.
  //
  // vista=canale, sempre: ogni scheda coi soli messaggi del suo canale (anteprima, ora, non letti, canali), e i
  // numerini delle schede per messaggio, anche in Tutti. Senza, il server risponde come alla v1.1.0 (conversazioni
  // intere, numerini per conversazione): e' come continuano a chiedere le app gia' installate, che il canale lo
  // mandano gia' con quel significato. Un backend che vista non la conosce la ignora.
  //
  // conti: i numerini delle schede, { nonLetti, perCanale, canali }, contati dal server su tutte le
  // conversazioni delle strutture chieste e non sulla sola pagina. Null da un backend che non li manda.
  //
  // archiviate: vero per le SOLE chat archiviate in Zoelia (vedi archiviaConversazione); falso, di serie,
  // la pagina senza di loro. Nella risposta, archiviate e' quante ce ne sono sul canale chiesto: e' il
  // numero accanto a "Archiviate" in cima all'elenco. Zero da un backend che l'archivio non lo conosce.
  async getConversationsPage(forza = false, proprieta = [], limite = null,
                             { allinea = true, canale = null, archiviate = false } = {}) {
    const { data } = await apiClient.get(`${BASE}/conversations`, {
      params: {
        forza,
        proprieta: proprieta.length ? proprieta.join(",") : undefined,
        limite: limite == null ? undefined : limite,
        allinea: allinea ? undefined : false,
        canale: canale || undefined,
        archiviate: archiviate ? true : undefined,
        vista: "canale",
      },
    });
    const d = data?.data;
    if (Array.isArray(d)) return { conversazioni: d, altre: false, conti: null, archiviate: 0 };
    const conti = d?.nonLettiPerCanale
      ? { nonLetti: Number(d.nonLetti) || 0, perCanale: d.nonLettiPerCanale, canali: d.canali || [] }
      : null;
    return { conversazioni: d?.conversazioni || [], altre: !!d?.altre, conti, archiviate: Number(d?.archiviate) || 0 };
  },
  // La conversazione di una singola prenotazione, per il suo dettaglio: torna
  // { conversazione, messaggi }, con conversazione nulla se non e' mai stata aperta.
  //
  // skipToast: per le riletture di sfondo (un annuncio, la riconnessione), che nessuno ha chiesto e che
  // non devono mostrare un errore di connessione a chi sta solo guardando la scheda.
  async getReservationConversation(propertyId, reservationId, { skipToast = false } = {}) {
    const { data } = await apiClient.get(
      `${BASE}/properties/${struttura(propertyId)}/reservations/${reservationId}/conversation`, { skipToast });
    return data?.data || { conversazione: null, messaggi: [] };
  },
  /**
   * Segna letta la conversazione. canale ("OTA" o "WHATSAPP"): solo i messaggi di quel canale, anche in
   * una conversazione che vive su tutti e due (aprirla dalla scheda Portali non legge i WhatsApp, che li'
   * non si vedono); null, tutta la conversazione.
   */
  async markConversationRead(propertyId, conversationId, { canale = null } = {}) {
    const { data } = await apiClient.post(
      `${BASE}/properties/${struttura(propertyId)}/conversations/${encodeURIComponent(conversationId)}/letta`,
      undefined, { params: { canale: canale || undefined } });
    return data?.data;
  },
  // sospendi=true zittisce l'automatismo in quella conversazione, false lo riattiva.
  async cambiaStatoAgente(propertyId, conversationId, sospendi) {
    const { data } = await apiClient.post(
      `${BASE}/properties/${struttura(propertyId)}/conversations/${encodeURIComponent(conversationId)}/agente`,
      null, { params: { sospendi } });
    return data?.data;
  },
  // attiva=true traduci qui, false non tradurre qui, null "come dice la struttura".
  async impostaTraduzione(propertyId, conversationId, attiva) {
    const { data } = await apiClient.post(
      `${BASE}/properties/${struttura(propertyId)}/conversations/${encodeURIComponent(conversationId)}/traduzione`,
      null, { params: attiva === null ? {} : { attiva } });
    return data?.data;
  },
  /**
   * Sceglie a mano la lingua dell'ospite di una conversazione ("de", "zh-Hans": i codici di lingueOspite): da
   * li' i messaggi dell'ospite non la cambiano piu', e le risposte tradotte partono in quella; la usano anche i
   * messaggi automatici delle regole che traducono e le regole della casa che l'ospite apre dal suo link. null torna
   * ad "Automatica", cioe' al rilevamento. Risponde { linguaOspite, linguaSceltaAMano, messaggiRiscritti,
   * riscritturaIncompleta }: i primi due da copiare sulla conversazione, e non serve rileggerla (i messaggi gia' a
   * schermo non si ritraducono). messaggiRiscritti e' quanti messaggi automatici gia' programmati per la prenotazione
   * il server ha riscritto nella lingua nuova: puo' mancare, e allora vale zero. riscritturaIncompleta e' true se
   * qualcuno e' rimasto nella lingua di prima (traduttore giu' o troppo lento, un messaggio che stava partendo): la
   * scelta e' salvata lo stesso, e rimandare la stessa lingua riprova a riscriverli.
   */
  async scegliLinguaOspite(propertyId, conversationId, lingua) {
    const { data } = await apiClient.put(
      `${BASE}/properties/${struttura(propertyId)}/conversations/${encodeURIComponent(conversationId)}/lingua`,
      { lingua: lingua || null });
    return data?.data;
  },
  /**
   * Le lingue che si possono scegliere per l'ospite: { comuni: [...], altre: [...] }, solo i codici (i nomi li
   * scrive la schermata, nella lingua di chi guarda). Sono quelle del traduttore e non cambiano mentre la pagina
   * e' aperta: si chiedono una volta sola, e chi le chiede insieme aspetta la stessa risposta. Una lettura
   * fallita non resta in memoria, cosi' "Riprova" riparte davvero. Senza avviso automatico: l'errore lo dice il
   * pannello che le mostra, accanto al suo "Riprova".
   */
  lingueOspite() {
    if (!lingueOspiteInVolo) {
      lingueOspiteInVolo = apiClient.get(`${BASE}/conversations/lingue`, { skipToast: true })
        .then(({ data }) => ({
          comuni: Array.isArray(data?.data?.comuni) ? data.data.comuni : [],
          altre: Array.isArray(data?.data?.altre) ? data.data.altre : [],
        }))
        .catch((e) => {
          lingueOspiteInVolo = null;
          throw e;
        });
    }
    return lingueOspiteInVolo;
  },
  // Archivia la conversazione su Channex. Senza ritorno: chiedere conferma prima.
  async chiudiConversazione(propertyId, conversationId) {
    const { data } = await apiClient.post(
      `${BASE}/properties/${struttura(propertyId)}/conversations/${encodeURIComponent(conversationId)}/chiudi`);
    return data?.data;
  },
  // I soggiorni che potrebbero essere di una chat WhatsApp non ancora attribuita.
  // Senza q: quelli del numero della chat (nessuno per una chat lid:, che il numero non ce l'ha).
  // Con q: la ricerca libera per nome, codice o data (gg/mm/aaaa), fra le strutture che si vedono.
  async candidateConversazione(propertyId, conversationId, q = null) {
    const cerca = typeof q === "string" ? q.trim() : "";
    const { data } = await apiClient.get(
      `${BASE}/properties/${struttura(propertyId)}/conversations/${encodeURIComponent(conversationId)}/candidate`,
      { params: cerca ? { q: cerca } : {} });
    return data?.data || [];
  },

  // Restituisce la chiave NUOVA: dopo l'associazione la conversazione cambia identificativo.
  async associaConversazione(propertyId, conversationId, reservationId) {
    const { data } = await apiClient.post(
      `${BASE}/properties/${struttura(propertyId)}/conversations/${encodeURIComponent(conversationId)}/associa`,
      { reservationId });
    return data?.data;
  },

  async sganciaConversazione(propertyId, conversationId) {
    const { data } = await apiClient.post(
      `${BASE}/properties/${struttura(propertyId)}/conversations/${encodeURIComponent(conversationId)}/sgancia`);
    return data?.data;
  },

  /**
   * Archivia in Zoelia una chat SOLO WhatsApp: esce dall'elenco finche' l'ospite non scrive di nuovo (un
   * nostro messaggio non la riporta). Si disfa con riapriConversazione. Una conversazione che passa anche
   * dal portale si archivia su Channex con chiudiConversazione: qui risponderebbe CONVERSAZIONE_CON_PORTALE.
   */
  async archiviaConversazione(propertyId, conversationId) {
    const { data } = await apiClient.post(
      `${BASE}/properties/${struttura(propertyId)}/conversations/${encodeURIComponent(conversationId)}/archivia`);
    return data?.data;
  },
  /** Rimette in elenco una chat archiviata in Zoelia. Ripetuta, non fa danni. */
  async riapriConversazione(propertyId, conversationId) {
    const { data } = await apiClient.post(
      `${BASE}/properties/${struttura(propertyId)}/conversations/${encodeURIComponent(conversationId)}/riapri`);
    return data?.data;
  },
  /**
   * "Togli da Zoelia" una chat WhatsApp da associare che non e' di un ospite: i suoi messaggi escono da
   * Zoelia e quelli dopo di quel numero non entrano piu' (salvo il codice di una prenotazione, una
   * prenotazione nuova con quel numero, o "Porta in Zoelia" nei Canali). Su WhatsApp non cambia niente.
   * Torna { tolti }: quanti messaggi sono stati cancellati da Zoelia.
   */
  async togliChat(propertyId, conversationId) {
    const { data } = await apiClient.post(
      `${BASE}/properties/${struttura(propertyId)}/conversations/${encodeURIComponent(conversationId)}/togli`);
    return data?.data;
  },

  async getUnreadCount() {
    // Il numerino sulla voce di menu si aggiorna da solo, senza che nessuno lo chieda:
    // se non ci riesce si tace e resta com'era. Vedi messaggiNonLetti.aggiorna().
    const { data } = await apiClient.get(`${BASE}/conversations/non-lette`, { skipToast: true });
    return data?.data?.nonLetti || 0;
  },
  /**
   * Segna letto tutto quello che c'e' da leggere.
   *
   * <p>Il server lo fa solo dentro l'ambito di chi chiede: chi segue due case non azzera il
   * non-letto delle altre diciotto.
   *
   * <p>canale ("WHATSAPP" o "OTA"): solo i messaggi di quel canale, anche nelle conversazioni che vivono
   * su tutti e due (i messaggi dell'altro canale restano da leggere): per questo va con vista=canale, che senza
   * il server segna la conversazione intera, come alla v1.1.0. proprieta: solo quelle strutture. Senza nessuno
   * dei due, la chiamata di sempre.
   */
  async segnaTutteLette({ canale = null, proprieta = [] } = {}) {
    const params = {
      canale: canale || undefined,
      vista: canale ? "canale" : undefined,
      proprieta: proprieta && proprieta.length ? proprieta.join(",") : undefined,
    };
    const { data } = await apiClient.post(`${BASE}/conversations/tutte-lette`, undefined, { params });
    return data?.data?.segnati || 0;
  },
  /**
   * Manda un testo e restituisce la bolla registrata, nella forma dell'elenco (o null, dall'OTA,
   * quando il server non l'ha): chi manda sostituisce con quella la sua bolla provvisoria.
   *
   * Per WhatsApp il corpo e' { canale: "WHATSAPP", conversazione, text, rispostaA?, idInvio? }: il
   * destinatario lo ricava il server dalla conversazione, e lo stesso idInvio ritrova la stessa bolla
   * invece di mandare due volte. Per l'OTA il corpo e' quello di sempre ({ threadId } oppure
   * { channexBookingId }, piu' text).
   */
  async sendMessage(propertyId, body) {
    const { data } = await apiClient.post(`${BASE}/properties/${struttura(propertyId)}/messages`, body);
    return data?.data;
  },

  // ==================== WhatsApp: media, reazioni, modifiche ====================

  /**
   * Manda dei file su WhatsApp, un messaggio per file; restituisce i Messaggi nell'ordine dei file.
   *
   * La chat ne manda UNO per richiesta, ciascuno col suo idInvio: il limite del multipart sul backend
   * e' 30 MB per richiesta, e cinque foto insieme lo superavano. Il metodo accetta comunque un
   * elenco, perche' il server lo accetta.
   *
   * Il Content-Type va dichiarato multipart a mano: l'istanza di api.js ha JSON di serie, e con
   * quello axios trasformerebbe il FormData in un oggetto JSON (e i file in niente). Dichiarato
   * multipart, axios lascia che il browser scriva l'intestazione vera, con il separatore.
   *
   * Gli errori NON li mostra api.js (skipToast): li mostra chi chiama. Un invio annullato col signal
   * arriverebbe all'interceptor come un errore senza risposta, e diventerebbe un "errore di connessione"
   * per un gesto che l'utente ha fatto apposta.
   *
   * @param dati { conversazione, testo, rispostaA, idInvio, comeDocumento, vocale }: testo e citazione
   *             valgono solo per il primo file
   * @param opzioni onUploadProgress per la barra della bolla, signal (AbortController) per annullare
   */
  async sendMedia(propertyId, files, dati, { onUploadProgress, signal } = {}) {
    const corpo = new FormData();
    (files || []).forEach((f) => corpo.append("file", f, f.name || "file"));
    corpo.append("dati", JSON.stringify(dati || {}));
    const { data } = await apiClient.post(`${BASE}/properties/${struttura(propertyId)}/messages/media`, corpo, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress,
      signal,
      skipToast: true,
    });
    return data?.data || [];
  },
  /** Mette, cambia o toglie (emoji vuota) la nostra reazione. Restituisce il Messaggio aggiornato. */
  async reagisci(messaggioId, emoji) {
    const { data } = await apiClient.put(`${BASE}/messaggi/${messaggioId}/reazione`, { emoji: emoji || "" });
    return data?.data;
  },
  /**
   * Modifica un nostro messaggio di testo (entro i minuti che dice la capacita' della chat). Una
   * modifica dall'esito incerto risponde col testo di prima: quello nuovo arriva dopo, con l'annuncio.
   *
   * skipToast: l'errore lo mostra chi chiama, che sa distinguere il falso errore del ritentativo di
   * api.js (la prima richiesta era passata, e la seconda trova la modifica gia' in attesa).
   */
  async modificaMessaggio(messaggioId, testo) {
    const { data } = await apiClient.put(`${BASE}/messaggi/${messaggioId}`, { testo }, { skipToast: true });
    return data?.data;
  },
  /** Elimina per tutti un nostro messaggio. Ripetuta su uno gia' eliminato vale come riuscita. */
  async eliminaMessaggio(messaggioId) {
    const { data } = await apiClient.delete(`${BASE}/messaggi/${messaggioId}`);
    return data?.data;
  },
  /**
   * Rimanda un nostro messaggio che non e' partito, con lo stesso id. skipToast come la modifica: se il
   * ritentativo di api.js trova la bolla gia' ripartita, non e' un errore da mostrare.
   */
  async riprovaMessaggio(messaggioId) {
    const { data } = await apiClient.post(`${BASE}/messaggi/${messaggioId}/riprova`, null, { skipToast: true });
    return data?.data;
  },
  /**
   * Lo stato del WhatsApp dello spazio: { presente, fornitore, stato, statoSessione, numero,
   * daRicollegare, restrizione, capacita }.
   *
   * Lo chiedono insieme il banner e la casella di risposta, all'apertura e a ogni evento "whatsapp":
   * una richiesta gia' in volo si condivide invece di partirne una seconda uguale, e una risposta di
   * meno di mezzo minuto fa si riusa (vedi statoWhatsappLetto: un annuncio "whatsapp" la butta). Di
   * contorno, quindi senza avviso a schermo se non risponde: chi la usa resta com'era.
   */
  statoWhatsapp() {
    if (statoWhatsappInVolo) return statoWhatsappInVolo;
    if (statoWhatsappLetto && Date.now() - statoWhatsappLetto.alle < STATO_WHATSAPP_VALE_MS) {
      return Promise.resolve(statoWhatsappLetto.valore);
    }
    const lettura = apiClient.get(`${BASE}/whatsapp/stato`, { skipToast: true })
      .then(({ data }) => {
        const valore = data?.data || null;
        // Solo se nel frattempo non e' arrivato un annuncio: quello dice che la risposta e' gia' vecchia.
        if (statoWhatsappInVolo === lettura) statoWhatsappLetto = { alle: Date.now(), valore };
        return valore;
      })
      .finally(() => {
        if (statoWhatsappInVolo === lettura) statoWhatsappInVolo = null;
      });
    statoWhatsappInVolo = lettura;
    return lettura;
  },
  /**
   * Come si apre la chat WhatsApp di una prenotazione: { link, testo, codice, chatAperta }. Il link
   * wa.me col codice lo apre l'ospite: da Zoelia non parte mai un primo messaggio a freddo.
   *
   * Di contorno anche questa (skipToast): la scheda della prenotazione la legge a ogni apertura per
   * decidere cosa mostrare accanto al telefono, e se non risponde la riga semplicemente non compare,
   * senza un avviso d'errore che nessuno ha chiesto.
   */
  async linkWhatsapp(propertyId, reservationId) {
    const { data } = await apiClient.get(
      `${BASE}/properties/${struttura(propertyId)}/reservations/${reservationId}/whatsapp/link`, { skipToast: true });
    return data?.data || null;
  },

  // ==================== Modelli dalla casella di risposta ====================

  /**
   * I modelli di messaggio attivi dello spazio (quelli di Impostazioni > Modelli di messaggio), per nome:
   * [{ id, nome, testo, lingua }], con le {{variabili}} ancora scritte nel testo. q: parole che devono
   * comparire tutte nel nome o nel testo, senza badare a maiuscole e accenti; vuoto, tutti.
   *
   * <p>La struttura e' quella della conversazione aperta: i modelli sono dello spazio, ma il server
   * controlla che chi chiede la veda. skipToast: e' una lettura di contorno, e se non risponde lo dice la
   * casella, nel suo elenco.</p>
   */
  async modelliRapidi(propertyId, q = null) {
    const cerca = typeof q === "string" ? q.trim() : "";
    const { data } = await apiClient.get(`${BASE}/properties/${struttura(propertyId)}/messages/modelli`,
      { params: cerca ? { q: cerca } : {}, skipToast: true });
    return Array.isArray(data?.data) ? data.data : [];
  },
  /**
   * Un modello reso per la casella: { id, nome, testo, lingua, tradotto, prenotazioneId, senzaValore[] }.
   * Il testo e' in italiano, con i dati della prenotazione (o della sola struttura, senza prenotazione): si
   * corregge e si manda con l'invio di sempre, che lo traduce nella lingua dell'ospite.
   *
   * @param prenotazione  l'UUID della prenotazione (conversazione.prenotazioneId, o la scheda aperta)
   * @param conversazione la chiave della conversazione: vale solo nella forma "prenotazione:<uuid>"
   */
  async modelloReso(propertyId, modelloId, { prenotazione = null, conversazione = null } = {}) {
    const { data } = await apiClient.get(
      `${BASE}/properties/${struttura(propertyId)}/messages/modelli/${encodeURIComponent(modelloId)}`,
      { params: { prenotazione: prenotazione || undefined, conversazione: conversazione || undefined } });
    return data?.data || null;
  },
};
